import { createMemoryHistory } from 'history';
import {
  searchPartToSubTarget,
  subTargetToSearchPart,
  pathnameToTarget,
  targetToPathname,
} from './locationTranslators';
import expandSimplifiedTarget from './expandSimplifiedTarget';
import annotationService from '../../../services/AnnotationService';
import { IDENTIFYING } from '../../../services/AnnotationService/constants';

class RouteProvider {
  constructor() {
    this.subscribers = [];
    this.initialize();
  }

  initialize() {
    this.history = createMemoryHistory();
    this.history.listen(this.onHistoryChange);
    this.onHistoryChange(this.history.location);
  }

  getCurrentTarget = async (location) => {
    const target = pathnameToTarget(location.pathname);
    const simplifiedTarget = {
      ...target,
      subTarget: location.search ? searchPartToSubTarget(location.search) : undefined,
    };

    // TODO: Add target validation and error handling to revert invalid location changes
    let currentTarget = await expandSimplifiedTarget(simplifiedTarget);

    const {
      id,
      ancestry,
      subTarget,
    } = currentTarget;

    if (subTarget?.ref && subTarget.elementRef) {
      return currentTarget;
    }

    if (subTarget?.ref && ancestry?.length) {
      // Retrieve identifying annotations for deployment
      const annotations = await annotationService.getAnnotationsByMotivation(IDENTIFYING, { id: ancestry[0].elementId });

      // Search thru annotations for a match for the current screen and subTarget ref field
      const findMatchingIdentifyingRef = (body) => body.purpose === IDENTIFYING && body.value === subTarget.ref;
      const findMatchingAnnotation = (annotation) => annotation.target[0].id === id && annotation.body.find(findMatchingIdentifyingRef);
      const matchingAnnotation = annotations.find(findMatchingAnnotation);

      // If found replace the subTarget ref text value, with the matched elementRef value
      const elementRef = matchingAnnotation && matchingAnnotation.target[0].subTarget.elementRef;
      if (elementRef) {
        currentTarget = {
          ...currentTarget,
          subTarget: {
            elementRef,
          },
        };
      } else {
        // Otherwise push to the target to remove the invalid subtarget
        this.push(target, {});
      }
    }

    return currentTarget;
  }

  onHistoryChange = async (location) => {
    this.currentTarget = await this.getCurrentTarget(location);
    this.notifySubscribers();
  }

  subscribe = (listener) => {
    this.subscribers.push(listener);
    if (this.currentTarget) {
      listener(this.currentTarget);
    }
    return () => this.unsubscribe(listener);
  }

  unsubscribe = (listener) => {
    if (this.subscribers.includes(listener)) {
      this.subscribers.splice(this.subscribers.indexOf(listener), 1);
    }
  }

  notifySubscribers = () => {
    this.subscribers.forEach((listener) => listener(this.currentTarget));
  }

  updateHistory = (target, subTarget, method = 'push') => {
    if (!this.history) {
      throw new Error('Route Provider hasn\'t been initialized yet');
    }

    const pathname = targetToPathname(target);
    const search = subTargetToSearchPart(subTarget);
    const { pathname: prevPathname, search: prevSearch } = this.history.location;
  
    if (pathname !== prevPathname || search !== prevSearch) {
      this.history[method]?.({ pathname, search });
    }
  }
  
  push = (target, subTarget) => {
    this.updateHistory(target, subTarget, "push");
  }

  replace = (target, subTarget) => {
    this.updateHistory(target, subTarget, "replace");
  }

}

export default new RouteProvider();
